import { useState, useEffect } from 'react'
import { HiArrowLeft } from 'react-icons/hi'
import { HiOutlineMenu } from 'react-icons/hi'

import { motion } from 'framer-motion'
import { apis } from '../api/apiUrl'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import AnimatedPage from '../components/animatedPage'

export default function SearchResults () {
  const [searchParams] = useSearchParams()
  const query = searchParams.get('query') || ''
  const [results, setResults] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const navigate = useNavigate()

  useEffect(() => {
    const fetchResults = async () => {
      if (!query) return

      try {
        const apiUrl = `${apis.uniten_api}/Search?query=${encodeURIComponent(
          query
        )}`
        const response = await fetch(apiUrl)
        const data = await response.json()

        if (data.result.success) {
          setResults(data.result.data)
        } else {
          setError(data.result.message || 'Search failed')
        }
      } catch (err) {
        setError('Failed to fetch search results')
        console.error('Search error:', err)
      } finally {
        setIsLoading(false)
      }
    }

    fetchResults()
  }, [query])

  if (isLoading) {
    return (
      <div className='min-h-screen bg-gray-50 flex items-center justify-center'>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className='text-center'
        >
          <motion.div
            animate={{ rotate: 360 }}
            transition={{
              duration: 1,
              repeat: Number.POSITIVE_INFINITY,
              ease: 'linear'
            }}
            className='w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4'
          />
          <p className='text-gray-600'>Searching...</p>
        </motion.div>
      </div>
    )
  }

  return (
    <AnimatedPage>
      {/* show the red bg div with 300px heigh */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className='bg-red-500 h-72 flex items-center justify-center'
      ></motion.div>
      <motion.main
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className='container mx-auto px-4 py-8'
      >
        <div className='mb-8'>
          <h1 className='text-5xl font-bold text-gray-900 mb-4'>
            Search Results
          </h1>
          <p className='text-gray-600 text-xl'>
            There are <strong>{results.length}</strong> results for{' '}
            <strong>&ldquo;{query}&rdquo;</strong>
          </p>
        </div>

        {error ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='text-center py-12'
          >
            <p className='text-red-600'>{error}</p>
          </motion.div>
        ) : results.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className='text-center py-12'
          >
            <p className='text-gray-600'>No results found for &ldquo;{query}&rdquo;</p>
          </motion.div>
        ) : (
          <div className='grid md:grid-cols-2 gap-6'>
            {results.map((result, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                onClick={() => navigate(`/${result.path}`)}
                className='bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md hover:border-gray-300 transition-all cursor-pointer'
              >
                <div className='mb-3'>
                  <span className='text-xs text-blue-600 uppercase tracking-wide font-semibold'>
                    {result.path.split('/').pop()?.replace(/-/g, ' ') ||
                      'General'}
                  </span>
                </div>
                <h3 className='font-semibold text-gray-900 mb-3 text-lg leading-tight'>
                  {result.preview}
                </h3>
                <p className='text-sm text-gray-500'>Path: {result.path}</p>
              </motion.div>
            ))}
          </div>
        )}
      </motion.main>
    </AnimatedPage>
  )
}
